import { supabase } from '@/integrations/supabase/client';

export interface Estimate {
  id: string;
  user_id: string;
  wave_estimate_id?: string;
  wave_sync_status?: 'pending' | 'synced' | 'failed';
  wave_sync_error?: string;
  wave_synced_at?: string;
  status: 'pending' | 'approved' | 'invoiced';
  line_items: Array<{
    description: string;
    quantity: number;
    rate: number;
    amount: number;
  }>;
  total: number;
  created_at: string;
  updated_at: string;
}

export interface Invoice {
  id: string;
  estimate_id?: string;
  user_id: string;
  wave_invoice_id?: string;
  wave_sync_status?: 'pending' | 'synced' | 'failed';
  wave_sync_error?: string;
  wave_synced_at?: string;
  status: 'unpaid' | 'paid' | 'overdue';
  scheduled_date?: string;
  due_date?: string;
  paid_at?: string;
  total: number;
  created_at: string;
  updated_at: string;
}

export interface Receipt {
  id: string;
  invoice_id: string;
  payment_amount: number;
  payment_date: string;
  wave_payment_id?: string;
  created_at: string;
}

export interface Contract {
  id: string;
  user_id: string;
  estimate_id: string;
  signed_at?: string;
  signature_data?: any;
  terms_accepted: boolean;
  contract_pdf_url?: string;
  created_at: string;
}

export const billingService = {
  // Wave Integration Functions
  async syncCustomerToWave(userId: string): Promise<void> {
    const { error } = await supabase.functions.invoke('sync-customer-to-wave', {
      body: { userId }
    });
    
    if (error) throw error;
  },

  async syncEstimateToWave(estimateId: string): Promise<void> {
    const { error } = await supabase.functions.invoke('sync-estimate-to-wave', {
      body: { estimateId }
    });
    
    if (error) throw error;
  },

  async syncInvoiceToWave(invoiceId: string): Promise<void> {
    const { error } = await supabase.functions.invoke('sync-invoice-to-wave', {
      body: { invoiceId }
    });
    
    if (error) throw error;
  },

  // New Wave Import Functions
  async importWaveCustomers(): Promise<any> {
    const { data, error } = await supabase.functions.invoke('import-wave-customers');
    if (error) throw error;
    return data;
  },

  async importWaveEstimates(): Promise<any> {
    const { data, error } = await supabase.functions.invoke('import-wave-estimates');
    if (error) throw error;
    return data;
  },

  async importWaveInvoices(): Promise<any> {
    const { data, error } = await supabase.functions.invoke('import-wave-invoices');
    if (error) throw error;
    return data;
  },

  async syncAllWaveData(): Promise<any> {
    const { data, error } = await supabase.functions.invoke('sync-all-wave-data');
    if (error) throw error;
    return data;
  },

  // Estimates
  async getEstimates(): Promise<Estimate[]> {
    const { data, error } = await supabase
      .from('estimates')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return (data || []).map(item => ({
      ...item,
      status: item.status as Estimate['status'],
      wave_sync_status: item.wave_sync_status as Estimate['wave_sync_status'],
      line_items: Array.isArray(item.line_items) ? item.line_items as Estimate['line_items'] : []
    }));
  },

  async updateEstimateStatus(id: string, status: Estimate['status']): Promise<void> {
    const { error } = await supabase
      .from('estimates')
      .update({ status })
      .eq('id', id);
    
    if (error) throw error;

    // Auto-sync to Wave when approved
    if (status === 'approved') {
      try {
        await this.syncEstimateToWave(id);
      } catch (syncError) {
        console.error('Failed to sync estimate to Wave:', syncError);
        // Don't throw - estimate update succeeded, sync can be retried later
      }
    }
  },

  // Invoices
  async getInvoices(): Promise<Invoice[]> {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return (data || []).map(item => ({
      ...item,
      status: item.status as Invoice['status'],
      wave_sync_status: item.wave_sync_status as Invoice['wave_sync_status']
    }));
  },

  async getScheduledInvoices(): Promise<Invoice[]> {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .not('scheduled_date', 'is', null)
      .order('scheduled_date', { ascending: true });
    
    if (error) throw error;
    return (data || []).map(item => ({
      ...item,
      status: item.status as Invoice['status'],
      wave_sync_status: item.wave_sync_status as Invoice['wave_sync_status']
    }));
  },

  async createInvoiceFromEstimate(estimateId: string, scheduledDate?: string): Promise<void> {
    const { data: estimate, error: estimateError } = await supabase
      .from('estimates')
      .select('*')
      .eq('id', estimateId)
      .single();
    
    if (estimateError) throw estimateError;

    const { data: invoice, error: invoiceError } = await supabase
      .from('invoices')
      .insert({
        estimate_id: estimateId,
        user_id: estimate.user_id,
        total: estimate.total,
        scheduled_date: scheduledDate,
        due_date: scheduledDate ? new Date(new Date(scheduledDate).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : undefined
      })
      .select()
      .single();

    if (invoiceError) throw invoiceError;

    // Update estimate status
    await this.updateEstimateStatus(estimateId, 'invoiced');

    // Auto-sync invoice to Wave
    try {
      await this.syncInvoiceToWave(invoice.id);
    } catch (syncError) {
      console.error('Failed to sync invoice to Wave:', syncError);
      // Don't throw - invoice creation succeeded, sync can be retried later
    }
  },

  async updateInvoiceSchedule(id: string, scheduledDate: string): Promise<void> {
    const { error } = await supabase
      .from('invoices')
      .update({ scheduled_date: scheduledDate })
      .eq('id', id);
    
    if (error) throw error;
  },

  // Receipts
  async getReceipts(): Promise<Receipt[]> {
    const { data, error } = await supabase
      .from('receipts')
      .select('*')
      .order('payment_date', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  // Contracts
  async getContracts(): Promise<Contract[]> {
    const { data, error } = await supabase
      .from('contracts')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async signContract(estimateId: string, signatureData: any): Promise<void> {
    const { error } = await supabase
      .from('contracts')
      .insert({
        user_id: (await supabase.auth.getUser()).data.user?.id!,
        estimate_id: estimateId,
        signed_at: new Date().toISOString(),
        signature_data: signatureData,
        terms_accepted: true
      });

    if (error) throw error;

    // Update estimate status to approved (this will auto-sync to Wave)
    await this.updateEstimateStatus(estimateId, 'approved');
  }
};
